import { useEffect, useState } from "react";
import {
  createFileRoute,
  Outlet,
  useLocation,
  useMatch,
  useNavigate,
} from "@tanstack/react-router";
import { I18nextProvider } from "react-i18next";

import type { BlogLocale } from "@/features/blog/locales";
import { createLandingI18n } from "@/common/i18n";
import { isAlternateLocale } from "@/features/blog/locales";
import { useSessionAuthState } from "@/hooks/useSession";
import { useUser } from "@/hooks/useUser";
import { CliFooter } from "@/landing/components/CliFooter";
import { CliNav } from "@/landing/components/CliNav";
import { WondatwinFooter } from "@/landing/components/WondatwinFooter";
import gridThemeCss from "@/landing/styles/grid-theme.css?url";
import { stewardFontLinks } from "@/landing/styles/steward-fonts";
import stewardThemeCss from "@/landing/styles/steward-theme.css?url";

export const Route = createFileRoute("/_landing")({
  component: LandingLayout,
  head: () => ({
    links: [
      { rel: "stylesheet", href: stewardThemeCss },
      { rel: "stylesheet", href: gridThemeCss },
      ...stewardFontLinks,
      // Fraunces italic — the /wab marketing display serif, used for the light
      // hero-video caption. Loaded on the landing only (not the app shells).
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@1,9..144,400&display=swap",
      },
    ],
  }),
});

function detectLocale(pathname: string): BlogLocale {
  const firstSegment = pathname.split("/")[1] ?? "";
  return isAlternateLocale(firstSegment) ? firstSegment : "en";
}

/**
 * A signed-in user who never completed onboarding doesn't get to browse the
 * marketing site — they're sent back into the flow (mirrors the _wonda layout
 * gate on product routes). Effect-based, not beforeLoad: the landing must
 * paint immediately for anonymous traffic, so the redirect rides the session
 * queries the nav already runs instead of blocking on identity resolution.
 * Waits for a provisioned identity (accountId) so a mid-provisioning user
 * isn't misread as not-onboarded.
 */
// Rendered CLIENT-ONLY. It reads the session via provider-dependent hooks
// (useSessionAuthState -> a gateway useQuery, useUser). `_landing` is SSR'd
// (unlike the ssr:false authenticated tree), but ClientProviders mounts no
// QueryClient on the server — so running these during SSR throws
// "No QueryClient set". StandardLandingLayout only mounts this after hydration,
// mirroring CliNav's NavAuthGate.
function NotOnboardedRedirect() {
  const navigate = useNavigate();
  const { isSignedIn } = useSessionAuthState();
  const { user } = useUser();

  useEffect(() => {
    if (!isSignedIn || !user) return;
    if (!user.publicMetadata?.accountId) return;
    if (user.publicMetadata.cliWelcomeCompleted) return;
    void navigate({ to: "/onboarding", replace: true });
  }, [isSignedIn, user, navigate]);

  return null;
}

function LandingLayout() {
  const { pathname } = useLocation();
  const locale = detectLocale(pathname);
  const [i18n] = useState(() => createLandingI18n(locale));
  // The standalone wondatwin.com product serves the LinkedIn landing at "/" — the index
  // route's loader resolves the host isomorphically, so reading its loaderData
  // is SSR-correct (the nav renders that page's section links from first paint,
  // with no swap on hydration).
  const indexMatch = useMatch({ from: "/_landing/", shouldThrow: false });
  const isWondatwinRoot = indexMatch?.loaderData?.isWondatwin === true;
  const isLinkedinLanding = pathname === "/linkedin" || isWondatwinRoot;

  // Sync language during render, before paint, so useTranslation() consumers
  // never see the previous locale when the URL locale changes. Bundled
  // resources make changeLanguage synchronous — safe to call in render.
  if (i18n.language !== locale) {
    void i18n.changeLanguage(locale);
  }

  if (isWondatwinRoot) {
    return (
      <I18nextProvider i18n={i18n}>
        <div className="steward-theme" style={{ minHeight: "100vh" }}>
          <div className="relative z-10">
            <CliNav variant="wondatwin" />
            <main>
              <Outlet />
            </main>
            <WondatwinFooter />
          </div>
        </div>
      </I18nextProvider>
    );
  }

  return (
    <StandardLandingLayout
      i18n={i18n}
      isLinkedin={isLinkedinLanding}
      isHome={indexMatch !== undefined}
    />
  );
}

function StandardLandingLayout({
  i18n,
  isLinkedin,
  isHome,
}: {
  i18n: ReturnType<typeof createLandingI18n>;
  isLinkedin: boolean;
  isHome: boolean;
}) {
  // Gate the auth redirect to client-only — its hooks need providers that are
  // not mounted during SSR (see NotOnboardedRedirect).
  const [isClient, setIsClient] = useState(false);
  useEffect(() => setIsClient(true), []);

  return (
    <I18nextProvider i18n={i18n}>
      {isClient && <NotOnboardedRedirect />}
      {/* grid-theme: the Grid design system (tokens in grid-theme.css, parts in
          landing/system). steward-theme stays underneath for pages not yet
          moved over. */}
      <div className="steward-theme grid-theme" style={{ minHeight: "100vh" }}>
        <div className="relative z-10">
          {/* /linkedin uses the same page design as the Wonda Twin root and
              swaps the nav to that page's own section links. Its hero sits on
              the same light ground as the rest of the landing, so the nav keeps
              its default dark-on-light marks (no overHero). */}
          <CliNav variant={isLinkedin ? "linkedin" : "default"} />
          <main>
            <Outlet />
          </main>
          {/* The homepage is the hero alone, one screen with no scroll. */}
          {!isHome && <CliFooter />}
        </div>
      </div>
    </I18nextProvider>
  );
}
